/**
 * Shared types for optisigns-infras-app.
 *
 * Two families:
 *   1. Provenance / scan machinery — the columns every scanned row carries and
 *      the scan-run + change-event shapes the ingest layer produces.
 *   2. Scanner payloads — the NORMALIZED shape each scanner emits per entity,
 *      which the IngestService upserts. Kept deliberately small (only fields we
 *      store), mirroring the "only what we read" discipline from staff-app.
 *
 * Consumed as TS source by both apps/api and apps/web (no build step).
 */

// ── scan machinery ───────────────────────────────────────────────────────────

export type ScannerName =
  | 'gitlab'
  | 'k8s'
  | 'gcp_secrets'
  | 'aws_secrets'
  | 'common_env'
  | 'repo_deps'
  | 'stitch'
  // scan_runs.scanner only. Rows it writes carry source_scanner='k8s' —
  // see migration 032 for why the two differ.
  | 'k8s_metrics'
  // The QA E2E seed (storage/seed-e2e/). Distinct so a seeded row is
  // tellable from a scanned one in the scan-run feed — see migration 037.
  | 'e2e_fixture'
  // DigitalOcean managed databases + their trusted-source firewall rules,
  // read-only, per DO team — see migration 052.
  | 'do_db_firewall';

export type Reachability = 'reachable' | 'pending_external';

/**
 * Live cluster health — a READ-THROUGH view from the central Prometheus (via the
 * Grafana SA-token proxy), NOT stored in the scan tables. `monitored:false` means
 * the cluster has no Alloy/scrape onboarded (or Grafana is unconfigured) — show
 * "not monitored", never "down". All metric fields null when unknown.
 */
export interface ClusterHealth {
  monitored: boolean;
  scrapeUpRatio: number | null; // up==1 / up, 0..1
  nodesReady: number | null;
  nodesTotal: number | null;
  podsCrashlooping: number | null;
  cpuUsedPct: number | null; // 0..100
  memUsedPct: number | null; // 0..100
  certExpiryDays: number | null; // best-effort (probe_ssl_*)
  queriedAt: string; // ISO — when the read-through last refreshed
}

export type Confidence = 'confirmed' | 'inferred' | 'manual';

export type ScanStatus = 'running' | 'succeeded' | 'failed';

/**
 * `job_run.status` as the scheduler writes it. Same vocabulary as ScanStatus
 * plus 'cancelled'.
 *
 * ⚠ It is **'succeeded'**, not 'success'. `'success'` is the Prometheus and
 * GitLab-pipeline spelling (see inventory-core `prom.client.ts` /
 * `pipeline-secrets.client.ts`) and comparing against it silently classifies
 * every healthy run as a failure — which is exactly what the first cut of the
 * freshness pill did. Use `isGoodRunStatus` instead of writing the literal.
 */
export type JobRunStatus = 'running' | 'succeeded' | 'failed' | 'cancelled';

/**
 * Did this run leave the data usable? 'running' counts as good so a long scan in
 * flight doesn't read as a problem. 'cancelled' does not: the data wasn't
 * refreshed, whatever the intent was.
 */
export function isGoodRunStatus(status?: string | null): boolean {
  return status === 'succeeded' || status === 'running';
}

export type ChangeKind = 'added' | 'updated' | 'disappeared' | 'reappeared';

export interface ScanRun {
  id: number;
  scanner: ScannerName;
  startedAt: string;
  finishedAt: string | null;
  status: ScanStatus;
  /** { entity: count } tallies + any per-scanner extras. */
  stats: Record<string, unknown>;
  triggeredBy: string;
}

export interface ChangeEvent {
  id: number;
  scanRunId: number;
  entityType: string;
  entityId: number;
  change: ChangeKind;
  /** { col: { from, to } } for updates; full row for added/disappeared. */
  diff: Record<string, unknown>;
  occurredAt: string;
}

// ── entity payloads (normalized scanner output) ──────────────────────────────

/** GitLab project → `repos`. Natural key: gitlabProjectId. */
export interface RepoPayload {
  gitlabProjectId: number;
  pathWithNamespace: string;
  groupPath: string;
  visibility: 'private' | 'internal' | 'public';
  defaultBranch: string | null;
  archived: boolean;
  lastActivityAt: string | null;
}

/** GitLab group member → `owners`. Natural key: gitlabUsername. */
export interface OwnerPayload {
  gitlabUsername: string;
  displayName: string | null;
  email: string | null;
  /** where the owner was learned from, e.g. 'gitlab-group-members'. */
  source: string;
}

/** K8s cluster → `clusters`. Natural key: name. */
export interface ClusterPayload {
  name: string;
  doAccount: string;
  region: string | null;
  k8sVersion: string | null;
  lbIp: string | null;
  /** { count, sizes: {instanceType: count} } from node labels. */
  nodeInfo: Record<string, unknown>;
  ingressType: string | null;
  /** 'cert-manager' when the cert-manager ns is present; else null (unknown). */
  certMode: string | null;
  alloyOnboarded: boolean;
  reachability: Reachability;
}

/** K8s namespace → `namespaces` (child of a cluster). Natural key: cluster + name. */
export interface NamespacePayload {
  clusterName: string;
  name: string;
}

// ── entity view models (API → web) ───────────────────────────────────────────
// The provenance columns surface on read so the UI can show freshness/confidence.

export interface Provenance {
  sourceScanner: ScannerName;
  firstSeenAt: string;
  lastSeenAt: string;
  confidence: Confidence;
  deletedAt: string | null;
}

export interface Repo extends RepoPayload, Provenance {
  id: number;
}

export interface Owner extends OwnerPayload, Provenance {
  id: number;
}
export * from './mcp';
export * from './graph';
export * from './catalog';
export * from './rbac';
export * from './access-requests';
export * from './agents';
export * from './atlas';
export * from './week';
export * from './error-codes';
export * from './vpn';
export * from './label-color';
